import { useEffect, useRef, useState } from "react";

const ANIM_MS = 300;

const RegistrationModal = ({ isOpen, onClose, children }) => {
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const [isVisible, setIsVisible] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsClosing(false);
      previousFocusRef.current = document.activeElement;
      // wait one frame so the panel is in the DOM before focusing
      const raf = requestAnimationFrame(() => {
        const focusable = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable?.length) focusable[0].focus();
      });
      return () => cancelAnimationFrame(raf);
    } else if (isVisible) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsClosing(false);
        previousFocusRef.current?.focus();
      }, ANIM_MS);
      return () => clearTimeout(timer);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const focusable = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable?.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isVisible) return null;

  const panelClass = isClosing ? "modal-panel-out" : "modal-panel-in";
  const overlayClass = isClosing ? "modal-overlay-out" : "modal-overlay-in";

  return (
    <div
      className="fixed z-50 inset-0 overflow-y-auto"
      aria-labelledby="reg-modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className={`fixed inset-0 bg-black ${overlayClass}`}
          style={{ opacity: 0 }}
          onClick={onClose}
          aria-hidden="true"
        />
        <div
          ref={modalRef}
          className={`bg-white rounded-lg overflow-hidden shadow-xl w-full relative ${panelClass}`}
          style={{ maxWidth: 920, zIndex: 51, opacity: 0 }}
        >
          <div className="flex items-center justify-between bg-gray-300 px-4 py-3 sm:px-6">
            <h2
              id="reg-modal-title"
              className="text-lg font-semibold text-gray-900 m-0"
            >
              Product Registration
            </h2>
            <button
              onClick={onClose}
              aria-label="Close modal"
              className="text-gray-400 hover:text-gray-500 transition ease-in-out duration-150 cursor-pointer"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationModal;
